import { MAX_VOUCHER_HOTEL_DETAILS } from '@gty/common';
import type {
  UpdateVoucherHotelDetailsDto,
  UpsertVoucherHotelDetailDto,
  VoucherHotelDetailDto,
} from '@gty/common';

import { Field, TextInput } from '../ui/Modal';
import { Button } from '../ui/primitives';

/**
 * The "Hotel details" sub-form the voucher editor mounts (item 5).
 *
 * ## WHAT IT EDITS
 *
 * The voucher's OWN hotel-stay rows — staff-entered, not a snapshot, same
 * architecture as `VoucherTransportLegFields`: a row can be added, corrected
 * and removed. Document-only: nothing here reaches the booking or the money.
 *
 * REPLACES the old read-only "Stay / Services" card as the one hotel section
 * — see `VoucherEditor.tsx`'s own note on why that card is retired rather
 * than extended.
 *
 * ## THE PATCH IS ID-KEYED, NOT A WHOLE-LIST REPLACE
 *
 * `diffHotelDetails` mirrors `diffTransportLegs` exactly: a draft with no
 * `id` is a new row, a draft with an `id` whose fields changed is a
 * correction, a server row missing from the drafts was removed, and an
 * unchanged row is omitted entirely.
 *
 * ## DATES ARE PLAIN `YYYY-MM-DD`, NOT WALL-CLOCK DATE-TIMES
 *
 * Unlike transport legs' departure/arrival, a check-in/check-out date has no
 * time-of-day component and so no AM/PM ambiguity — a native
 * `<input type="date">` is unambiguous regardless of locale, so there is no
 * `WallClockPicker`-style replacement needed here.
 */

/** Matches the server's `@MaxLength` on the hotel name field. */
const NAME_MAX_LENGTH = 160;
/** Matches the server's `@MaxLength` on city/room-type/rooms. */
const SHORT_MAX_LENGTH = 120;
/** Matches the server's `@MaxLength` on meal plan. */
const MEAL_PLAN_MAX_LENGTH = 80;

/**
 * Printed directly below the Hotel Details table on the voucher PDF
 * (`VoucherDocument.tsx`), only when the table itself prints. One constant,
 * not a literal at the print call site, so the wording can be changed in
 * one place later.
 */
export const VOUCHER_HOTEL_DETAILS_NOTE =
  'Hotels may vary for each booking and are subject to change.';

/**
 * One hotel row as FORM STRINGS.
 *
 * `id` is the server's id, or `null` for a row added in this form and not yet
 * saved. `key` is the React key only — stable for a new row that has no id —
 * and is never sent. Dates are `''` while unknown; they become `null` in the
 * patch.
 */
export interface HotelDetailDraft {
  key: string;
  id: string | null;
  hotelName: string;
  city: string;
  checkInDate: string;
  checkOutDate: string;
  roomType: string;
  rooms: string;
  mealPlan: string;
}

export function toHotelDetailDrafts(rows: readonly VoucherHotelDetailDto[]): HotelDetailDraft[] {
  return rows.map((row) => ({
    key: row.id,
    id: row.id,
    hotelName: row.hotelName,
    city: row.city,
    checkInDate: row.checkInDate ?? '',
    checkOutDate: row.checkOutDate ?? '',
    roomType: row.roomType,
    rooms: row.rooms,
    mealPlan: row.mealPlan,
  }));
}

let nextNewRowKey = 0;

/** A blank row for "Add hotel". No `id` — the server assigns one on save. */
export function blankHotelDetailDraft(): HotelDetailDraft {
  nextNewRowKey += 1;
  return {
    key: `new-hotel-${nextNewRowKey}`,
    id: null,
    hotelName: '',
    city: '',
    checkInDate: '',
    checkOutDate: '',
    roomType: '',
    rooms: '',
    mealPlan: '',
  };
}

/**
 * Why ONE row cannot be saved yet, or `null` when it can.
 *
 * Mirrors the server: hotel name is required, and check-out — when given —
 * may not be before check-in. The comparison is on the raw `YYYY-MM-DD`
 * strings, which sort chronologically as text.
 */
export function hotelDetailProblem(row: HotelDetailDraft): string | null {
  if (row.hotelName.trim() === '') {
    return 'Enter a hotel name to save this row.';
  }
  if (row.checkInDate !== '' && row.checkOutDate !== '' && row.checkOutDate < row.checkInDate) {
    return 'Check-out cannot be before check-in.';
  }
  return null;
}

/**
 * Why the WHOLE set cannot be saved, or `null` when it can.
 *
 * The cap is enforced by the server under a row lock; this is only so the
 * editor explains itself instead of round-tripping for a 400.
 */
export function hotelDetailsBlockReason(drafts: readonly HotelDetailDraft[]): string | null {
  if (drafts.length > MAX_VOUCHER_HOTEL_DETAILS) {
    return `A voucher can carry at most ${MAX_VOUCHER_HOTEL_DETAILS} hotel rows — remove ${
      drafts.length - MAX_VOUCHER_HOTEL_DETAILS
    } to save.`;
  }
  const firstBad = drafts.findIndex((row) => hotelDetailProblem(row) !== null);
  if (firstBad >= 0) {
    return `Hotel row ${firstBad + 1}: ${hotelDetailProblem(drafts[firstBad] as HotelDetailDraft) ?? ''}`;
  }
  return null;
}

/** The draft row as the wire shape. `''` dates are an explicit `null` (unknown / cleared). */
function toUpsert(row: HotelDetailDraft): UpsertVoucherHotelDetailDto {
  return {
    ...(row.id !== null ? { id: row.id } : {}),
    hotelName: row.hotelName,
    city: row.city,
    checkInDate: row.checkInDate === '' ? null : row.checkInDate,
    checkOutDate: row.checkOutDate === '' ? null : row.checkOutDate,
    roomType: row.roomType,
    rooms: row.rooms,
    mealPlan: row.mealPlan,
  };
}

function rowChanged(original: VoucherHotelDetailDto, draft: HotelDetailDraft): boolean {
  return (
    draft.hotelName !== original.hotelName ||
    draft.city !== original.city ||
    (draft.checkInDate === '' ? null : draft.checkInDate) !== original.checkInDate ||
    (draft.checkOutDate === '' ? null : draft.checkOutDate) !== original.checkOutDate ||
    draft.roomType !== original.roomType ||
    draft.rooms !== original.rooms ||
    draft.mealPlan !== original.mealPlan
  );
}

/**
 * ONLY WHAT CHANGED, as an id-keyed upsert plus explicit removals. Exported
 * for its own test. Same convention as `diffTransportLegs`:
 *
 * - a draft with no `id` is a new row -> `upsert` without an id;
 * - a draft with an `id` whose fields differ from the server -> `upsert` with it;
 * - a server row with no matching draft was removed -> `removeIds`;
 * - an unchanged row is omitted entirely.
 *
 * Returns `null` when there is nothing to send.
 */
export function diffHotelDetails(
  original: readonly VoucherHotelDetailDto[],
  drafts: readonly HotelDetailDraft[],
): UpdateVoucherHotelDetailsDto | null {
  const byId = new Map(original.map((row) => [row.id, row]));
  const upsert: UpsertVoucherHotelDetailDto[] = [];
  const kept = new Set<string>();

  for (const draft of drafts) {
    if (draft.id === null) {
      upsert.push(toUpsert(draft));
      continue;
    }
    const row = byId.get(draft.id);
    // An id this voucher no longer holds: the editor re-seeds whenever the
    // server's set changes, so this is only reachable mid-refetch; skip
    // rather than send a doomed correction.
    if (row === undefined) continue;
    kept.add(draft.id);
    if (rowChanged(row, draft)) upsert.push(toUpsert(draft));
  }

  const removeIds = original.filter((row) => !kept.has(row.id)).map((row) => row.id);

  if (upsert.length === 0 && removeIds.length === 0) return null;
  return {
    ...(upsert.length > 0 ? { upsert } : {}),
    ...(removeIds.length > 0 ? { removeIds } : {}),
  };
}

/**
 * The rendered sub-form.
 *
 * ALWAYS rendered, even with no rows — unlike the old "Stay / Services" card
 * this section has an add button, and staff need it to enter the first
 * hotel.
 *
 * `disabled` is inherited from the editor's enclosing `<fieldset>` (which
 * also disables the add/remove buttons), so a lock or a view-only role
 * greys the whole section without this component knowing about permissions.
 */
export function VoucherHotelDetailFields({
  drafts,
  onChange,
  onAdd,
  onRemove,
}: {
  drafts: readonly HotelDetailDraft[];
  onChange: (key: string, changes: Partial<HotelDetailDraft>) => void;
  onAdd: () => void;
  onRemove: (key: string) => void;
}): JSX.Element {
  const atCap = drafts.length >= MAX_VOUCHER_HOTEL_DETAILS;

  return (
    <div className="border-t border-neutral-200 px-5 py-4">
      <h3 className="text-[11px] font-semibold uppercase leading-none tracking-caps text-neutral-600">
        Hotel details
      </h3>
      <p className="mt-1.5 text-[11.5px] leading-[1.5] text-neutral-600">
        Where the traveller stays, as printed on the voucher. Up to {MAX_VOUCHER_HOTEL_DETAILS} hotels.
      </p>

      {drafts.length > 0 && (
        <div className="mt-3 grid gap-4">
          {drafts.map((row, index) => {
            const n = index + 1;
            const problem = hotelDetailProblem(row);
            const checkOutBeforeCheckIn =
              row.checkInDate !== '' && row.checkOutDate !== '' && row.checkOutDate < row.checkInDate;
            return (
              <div
                key={row.key}
                className="grid gap-3 rounded-[8px] border border-neutral-300 p-3.5 sm:grid-cols-2"
              >
                {/*
                  Labelled per row ("Hotel 1 name") rather than by a column
                  header, so a screen reader and a keyboard user both know
                  which row they are editing.
                */}
                <Field label={`Hotel ${n} name`} required>
                  <TextInput
                    value={row.hotelName}
                    maxLength={NAME_MAX_LENGTH}
                    placeholder="The Oberoi, Bali"
                    onChange={(e) => {
                      onChange(row.key, { hotelName: e.target.value });
                    }}
                  />
                </Field>

                <Field label={`Hotel ${n} city`}>
                  <TextInput
                    value={row.city}
                    maxLength={SHORT_MAX_LENGTH}
                    placeholder="Bali"
                    onChange={(e) => {
                      onChange(row.key, { city: e.target.value });
                    }}
                  />
                </Field>

                <Field label={`Hotel ${n} check-in`} hint="Optional — leave empty when unknown.">
                  <TextInput
                    type="date"
                    value={row.checkInDate}
                    onChange={(e) => {
                      onChange(row.key, { checkInDate: e.target.value });
                    }}
                  />
                </Field>

                <Field
                  label={`Hotel ${n} check-out`}
                  hint="Optional — leave empty when unknown."
                  error={checkOutBeforeCheckIn ? 'Check-out cannot be before check-in.' : undefined}
                >
                  <TextInput
                    type="date"
                    value={row.checkOutDate}
                    aria-invalid={checkOutBeforeCheckIn}
                    onChange={(e) => {
                      onChange(row.key, { checkOutDate: e.target.value });
                    }}
                  />
                </Field>

                <Field label={`Hotel ${n} room type`} hint="Optional.">
                  <TextInput
                    value={row.roomType}
                    maxLength={SHORT_MAX_LENGTH}
                    placeholder="Deluxe Room"
                    onChange={(e) => {
                      onChange(row.key, { roomType: e.target.value });
                    }}
                  />
                </Field>

                <Field label={`Hotel ${n} rooms`} hint="Optional — count or room numbers.">
                  <TextInput
                    value={row.rooms}
                    maxLength={SHORT_MAX_LENGTH}
                    placeholder="2 rooms, 204 & 205"
                    onChange={(e) => {
                      onChange(row.key, { rooms: e.target.value });
                    }}
                  />
                </Field>

                <Field label={`Hotel ${n} meal plan`} hint="Optional.">
                  <TextInput
                    value={row.mealPlan}
                    maxLength={MEAL_PLAN_MAX_LENGTH}
                    placeholder="Breakfast"
                    onChange={(e) => {
                      onChange(row.key, { mealPlan: e.target.value });
                    }}
                  />
                </Field>

                <div className="flex flex-wrap items-end justify-between gap-3 sm:col-span-2">
                  {problem !== null && !checkOutBeforeCheckIn ? (
                    <span className="text-[11px] leading-[1.4] text-neutral-600">{problem}</span>
                  ) : (
                    <span />
                  )}
                  <Button
                    variant="ghost"
                    aria-label={`Remove hotel ${n}`}
                    onClick={() => {
                      onRemove(row.key);
                    }}
                  >
                    Remove hotel
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button variant="outline" disabled={atCap} onClick={onAdd}>
          Add hotel
        </Button>
        {atCap && (
          <span className="text-[11px] leading-[1.4] text-neutral-600">
            Maximum of {MAX_VOUCHER_HOTEL_DETAILS} hotels reached.
          </span>
        )}
      </div>
    </div>
  );
}
